import { motion } from 'motion/react';
import { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import ChevronDown from '@/app/components/questionnaireV2/icons/chevron-down';
import { ROUTE_LABS, ROUTE_LABS_ORDER_STEP_1 } from '@/constants/routes';
import { TopLevelOrderStatus } from '@/api/generated-catch-backend/model';
import { LABS_CATEGORY_NAME } from '../../constants';
import CategoryCardBase from './category-card-base';
import { DataCategoryCardProps } from './types';

/**
 * DataCategoryCard displays a health data category with dynamic routing logic.
 * For the Catch Labs category, it provides special handling based on lab order status:
 * - eligible: Routes to lab ordering flow
 * - ready_for_testing/pending_review: Routes to lab status page
 * - results_ready: Routes to review/edit mode
 * For other categories, it uses standard review/edit navigation.
 */
export const DataCategoryCard = memo(function DataCategoryCard({
  name,
  description,
  icon,
  progressValue,
  progressMax,
  lastUpdated,
  isComplete,
  isEditMode = false,
  onReviewClick,
  labOrderStatus,
}: DataCategoryCardProps) {
  const navigate = useNavigate();

  const isLabsCategory = name === LABS_CATEGORY_NAME;

  const getButtonText = () => {
    if (isLabsCategory) {
      if (labOrderStatus === TopLevelOrderStatus.eligible) {
        return 'Order Labs';
      } else if (labOrderStatus === TopLevelOrderStatus.ready_for_testing || labOrderStatus === TopLevelOrderStatus.pending_review) {
        return 'View Labs';
      } else if (labOrderStatus === TopLevelOrderStatus.results_ready) {
        return isEditMode ? 'Edit' : 'Review';
      }
      // Default fallback
      return 'View Labs';
    }
    return isEditMode ? 'Edit' : 'Review';
  };

  // Handle click - navigate appropriately based on category and lab status
  const handleClick = () => {
    if (isLabsCategory && labOrderStatus === TopLevelOrderStatus.eligible) {
      navigate(ROUTE_LABS_ORDER_STEP_1);
    } else if (isLabsCategory && labOrderStatus !== TopLevelOrderStatus.results_ready) {
      navigate(ROUTE_LABS);
    } else {
      onReviewClick();
    }
  };

  return (
    <CategoryCardBase
      name={name}
      description={description}
      icon={icon}
      progressValue={progressValue}
      progressMax={progressMax}
      lastUpdated={lastUpdated}
      isComplete={isComplete}
    >
      {/* Review/Edit/View Labs Button */}
      <motion.button
        onClick={handleClick}
        className="relative box-border flex h-[40px] w-full lg:max-w-72 shrink-0 items-center justify-center rounded-[100px] bg-[#183344] px-[20px]"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        transition={{ duration: 0.2 }}
        aria-label={`${getButtonText()} ${name}`}
        type="button"
      >
        <span className="text-[14px] leading-[1.4] font-bold text-white">{getButtonText()}</span>
        {isEditMode && <ChevronDown className="absolute right-5 h-4 w-4 text-white" />}
      </motion.button>
    </CategoryCardBase>
  );
});

export default DataCategoryCard;
